"use client";

import { Check, CircleCheck } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { FixedLayout } from "@/components/shared/fixed-layout";
import { useSignupStore } from "@/lib/store/use-signup-store";

const PARENT_REQUIRED_TERM_TITLE = `14세 미만 사용자 가입에 대한
보호자 동의를 위해 본인 인증을 진행해 주세요.`;

interface ParentRequiredTerm {
  id: string;
  title: string;
}

const PARENT_REQUIRED_TERMS: ParentRequiredTerm[] = [
  {
    id: "parent-required-term-1",
    title: "법정대리인으로서 14세 미만 아동의 가입에 동의합니다.",
  },
  {
    id: "parent-required-term-2",
    title: "아동의 개인정보 수집·이용 목적 및 항목에 동의합니다.",
  },
];

export function ParentRequiredTermStep() {
  const { parentKmcAgreed, setParentKmcAgreed, goToNextStep, goToPrevStep } =
    useSignupStore();

  const [selectedTerms, setSelectedTerms] = useState<string[]>(
    parentKmcAgreed ? PARENT_REQUIRED_TERMS.map(({ id }) => id) : []
  );

  const isAllTermsSelected =
    selectedTerms.length === PARENT_REQUIRED_TERMS.length;

  const handleSelectTerm = (termId: string) => {
    setSelectedTerms((prev) =>
      prev.includes(termId)
        ? prev.filter((id) => id !== termId)
        : [...prev, termId]
    );
  };

  const handleSelectAllTerms = () => {
    setSelectedTerms(
      selectedTerms.length === PARENT_REQUIRED_TERMS.length
        ? []
        : PARENT_REQUIRED_TERMS.map(({ id }) => id)
    );
  };

  const handleNext = () => {
    if (isAllTermsSelected) {
      setParentKmcAgreed(true);
      setSelectedTerms([]);
      goToNextStep();
    }
  };

  return (
    <FixedLayout
      title={PARENT_REQUIRED_TERM_TITLE}
      buttonText="본인 인증하기"
      buttonDisabled={!isAllTermsSelected}
      onSubmit={handleNext}
      onBack={goToPrevStep}
    >
      <div
        className="flex gap-3 border border-border rounded-md p-3 items-center"
        onClick={handleSelectAllTerms}
      >
        <CircleCheck
          stroke="white"
          fill="currentColor"
          className={cn(
            "size-6 transition-colors duration-200",
            isAllTermsSelected ? "text-primary" : "text-border"
          )}
        />
        <span className="text-sm font-medium text-muted-foreground">
          14세 미만 가입자 사용 동의
        </span>
      </div>
      <div className="flex flex-col gap-2 pt-3.5">
        {PARENT_REQUIRED_TERMS.map(({ id, title }) => (
          <div key={id} className="flex w-full pl-3 py-1.5 gap-3 items-center">
            <div
              onClick={() => handleSelectTerm(id)}
              className="flex items-center gap-3 flex-1"
            >
              <Check
                className={cn(
                  "size-6 transition-colors duration-200",
                  selectedTerms.includes(id) ? "text-primary" : "text-border"
                )}
              />
              <span className="text-sm font-medium text-muted-foreground">
                {title}
              </span>
            </div>
          </div>
        ))}
      </div>
    </FixedLayout>
  );
}
